import clsx from 'clsx';
import { ReactNode } from 'react';

interface Props extends BaseProps {
  align?: Alignment;
  children: string | number | ReactNode;
  classNames?: {
    container?: string;
    label?: string;
    value?: string;
  };
  direction?: FlexDirection;
  disabled?: boolean;
  opacity?: 'default' | 'full';
  position?: FlexPosition;
  required?: boolean;
  size?: StandardSizes;
  text: string;
  htmlFor?: string;
}

export const Label = (props: Props) => {
  const {
    align = 'left',
    children,
    classNames,
    direction = 'column',
    disabled = false,
    opacity = 'default',
    position = 'start',
    required,
    size = 'sm',
    text,
    htmlFor,
    ...rest
  } = props;
  const classes = useStyles(direction);

  const renderLabel = () => {
    if (!required) return text;
    return `${text} *`;
  };

  const renderLabelChildren = () => {
    if (typeof children !== 'string' && typeof children !== 'number')
      return children;
    return (
      <p
        aria-labelledby={text}
        className={clsx(
          classNames?.value ?? '',
          classes.value[size],
          classes.textAlign[align]
        )}
      >
        {children}
      </p>
    );
  };

  return (
    <div
      className={clsx(
        classNames?.container ?? '',
        classes.container.default,
        classes.container.direction[direction],
        classes.align[align],
        {
          [classes.container.position.right]:
            direction === 'row' && position === 'end',
        },
        {
          [classes.container.position.bottom]:
            direction === 'column' && position === 'end',
        }
      )}
      {...rest}
    >
      <label
        aria-label={text}
        aria-required={required}
        htmlFor={htmlFor}
        className={clsx(
          classNames?.label ?? '',
          classes.textAlign['left'],
          classes.label.default,
          classes.label.opacity[opacity],
          classes.label[size],
          { '!opacity-30': disabled }
        )}
      >
        {renderLabel()}
      </label>
      {renderLabelChildren()}
    </div>
  );
};

const useStyles = (direction: FlexDirection) => {
  const width = direction === 'column' ? 'w-full' : 'w-fit';
  const gap = direction === 'column' ? 'gap' : 'gap-2';
  const size = {
    sm: 'text-md',
    md: 'text-base',
    lg: 'text-lg',
  };

  return {
    align: {
      left: 'items-start',
      center: 'items-center',
      right: 'items-end',
    },
    container: {
      default: `relative flex flex-nowrap justify-center ${gap} w-fit`,
      direction: {
        row: 'flex-row',
        column: 'flex-col',
      },
      position: {
        bottom: 'flex-col-reverse',
        right: 'flex-row-reverse',
      },
    },
    label: {
      default: `${width}`,
      opacity: {
        default: 'opacity-80 dark:opacity-70',
        full: 'text-inherit',
      },
      ...size,
    },
    textAlign: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    value: {
      default: width,
      ...size,
    },
  };
};
